import { isFunction, isObjectEqual, isString } from '@impuls-coach/util-is';
import { group, groupEnd, log, warn } from 'helper/debug';
import noop from 'helper/noop';

export type StoreBindingStack = {
	listener: string;
	resolver: Function;
	rejector: Function;
	_id: string;
	_stack: number;
};

const StoreBindingsIds = 0;

const STORE_BINDINGS_THRESHOLD = 8;
export const THRESHOLD = STORE_BINDINGS_THRESHOLD;

const getPlural = (baseValue: string, count?: number) => {
	if (!count || count === 1) {
		return baseValue;
	}

	return `${baseValue}s`;
};

const messages = {
	instanciated: '%s: Instanciated with a threshold of %d',
	thresholdReached: '%s: Maximum threshold of %d reached',
	listening: '%s.add(): Listening to %s (%s)',
	canTakeUpdates: '%s: Can take update keys: %s',
	foundUpdateKeys: '%s: Found %d possible updatable %s',
	foundNoUpdateKeys: '%s: Found %d possible updatable %s, but none is matching',
	runUpdate: '%s.run(): Runs update for %s',
	callResolver: '%s.run(): Executing resolver for %s',
	callRejector: '%s.run(): Executing rejector for %s',
	callGlobalRejector: '%s.run(): Call %d global rejectors',
	noChanges: '%s.run(): No change detected from the previous store update',
};

/*
 * Helper for dynamic route handling in combination with
 * Redux, Redux Saga and React.
 * @class StoreBindings
 * @author Jan Biasi <jan.biasi@namics.com>
 * @type {StoreBindings}
 */
export default class StoreBindings {
	public _stackBindings: StoreBindingStack[];
	public _resolved: string[];
	public _id: number;
	public _name: string | null;
	public _runCount: number;
	public _lastStore: Object | undefined;
	public _globalRejectors: Function[];
	public _indexMap: {
		[key: string]: number;
	};

	constructor(name?: string) {
		this._name = name || null;
		this._stackBindings = [];
		this._globalRejectors = [];
		this._indexMap = {};
		this._resolved = [];
		this._id = StoreBindingsIds + 1;
		this._runCount = 0;

		log(messages.instanciated, this._logId, STORE_BINDINGS_THRESHOLD);
	}

	get _logId(): string {
		const name = this._name || this.constructor.name;
		return `StoreBindings(${name})`;
	}

	get id(): number {
		return this._id;
	}

	get size(): number {
		return this._stackBindings.length;
	}

	get resolved(): string[] {
		return this._resolved;
	}

	get stackBindingKeys(): string[] {
		return this._stackBindings.map((binding: StoreBindingStack) => {
			return binding.listener;
		});
	}

	get stackBindingIds(): string[] {
		return this._stackBindings.map((binding: StoreBindingStack) => {
			return binding._id;
		});
	}

	public _checkRunnerThreshold() {
		if (this._runCount > STORE_BINDINGS_THRESHOLD) {
			warn(messages.thresholdReached, this._logId, STORE_BINDINGS_THRESHOLD);
		}
	}

	public exportBindings(): string {
		return JSON.stringify(this._stackBindings);
	}

	public importBindings(exportedBindings: string): boolean {
		if (!isString(exportedBindings)) {
			return false;
		}

		try {
			const bindings = JSON.parse(exportedBindings);

			bindings.forEach((binding: StoreBindingStack) => {
				this.add(binding.listener, binding.resolver, binding.rejector);
			});

			return true;
		} catch (err) {
			return false;
		}
	}

	public addGlobalRejector(rejector: Function): StoreBindings {
		if (rejector && isFunction(rejector)) {
			this._globalRejectors.push(rejector);
		}

		return this;
	}

	public add(listener: string, resolver: Function = noop, rejector: Function = noop): StoreBindings {
		const resolverName = resolver.name || 'Anonym';
		const rejectorName = rejector.name || 'Anonym';
		const stackBindingId = `${listener}@[${resolverName}][${rejectorName}]`;

		const index = this._stackBindings.push({
			listener,
			resolver,
			rejector,
			_id: stackBindingId,
			_stack: this._id,
		});

		log(messages.listening, this._logId, listener, listener);

		this._indexMap[listener] = index;
		return this;
	}

	public getUpdatableFactory(store: any): any[] {
		if (!store) {
			return [];
		}

		const availableKeys = this.stackBindingKeys;
		const updatedKeys = Object.keys(store);
		const updateable: any[] = [];

		if (availableKeys.length > 0) {
			log(messages.canTakeUpdates, this._logId, availableKeys.join(', ') || '-');
			log(messages.foundUpdateKeys, this._logId, updatedKeys.length, getPlural('key', updatedKeys.length));
		} else {
			log(messages.foundNoUpdateKeys, this._logId, updatedKeys.length, getPlural('key', updatedKeys.length));
		}

		updatedKeys.forEach((key: string) => {
			if (~availableKeys.indexOf(key)) {
				updateable.push({
					listener: key,
					decide: store[key],
				});
			}
		});

		return updateable;
	}

	public run(store: any, callback: Function = noop): void {
		const lastStore = this._lastStore;
		group(`${this._logId}.run(...)`);

		if (isObjectEqual(lastStore, store)) {
			log(messages.noChanges, this._logId);
			groupEnd(`${this._logId}.run(...)`);
			return void 0;
		}

		const updateable = this.getUpdatableFactory(store);

		if (updateable.length === 0) {
			groupEnd(`${this._logId}.run(...)`);
			return void 0;
		}

		log(messages.runUpdate, this._logId, updateable.map((updt) => updt.listener).join(', '));

		this._runCount++;
		this._checkRunnerThreshold();

		updateable.forEach((updateFactory: any) => {
			const targetIndex = this._indexMap[updateFactory.listener];
			const bindings = this._stackBindings[targetIndex - 1];
			group(`${this._logId}.run([${bindings._id}])`);

			if (updateFactory.decide) {
				log(messages.callResolver, this._logId, bindings._id);
				bindings.resolver.apply(this);
				this._resolved.push(bindings.listener);
			} else {
				log(messages.callRejector, this._logId, bindings._id);
				bindings.rejector.apply(this);

				if (this._globalRejectors.length > 0) {
					log(messages.callGlobalRejector, this._logId, this._globalRejectors.length);
					this._globalRejectors.forEach((rejector: Function) => rejector.apply(this, [bindings]));
				}
			}
			groupEnd(`${this._logId}.run([${bindings._id}])`);
		});

		callback.apply(this, [this.resolved]);
		groupEnd(`${this._logId}.run(...)`);
	}
}
